/**
 * @fileoverview Masked referral link route for KOL Manager referrals
 * @module routes/join.$slug
 *
 * Provides clean, non-obvious referral URLs for KOL Managers.
 * Instead of `/?manager_ref=crypto-kings`, managers can share
 * `onicorn.com/join/crypto-kings` which looks like a landing page.
 *
 * Flow:
 * 1. User visits /join/crypto-kings
 * 2. Save manager ref to localStorage
 * 3. Redirect to clean homepage /
 */

import { useEffect, type ReactElement } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { saveManagerReferral } from '@/features/kol-managers';

/**
 * Join redirect page component.
 *
 * Saves the manager referral code to localStorage and immediately
 * redirects to the homepage. The user sees a brief loading state
 * while the redirect happens.
 *
 * @component
 */
function JoinRedirectPage(): ReactElement {
  const { slug } = useParams({ from: '/join/$slug' });
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      // Save the manager referral to localStorage
      saveManagerReferral(slug);
    }

    // Redirect to homepage
    navigate({ to: '/', replace: true });
  }, [slug, navigate]);

  // Brief loading state while redirect happens
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto" />
        <p className="text-gray-400 mt-4">Redirecting...</p>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/join/$slug')({
  component: JoinRedirectPage,
});
